import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { ErrorLink } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { GQL_API } from '../config';
import { normalizeMessage, toOperationFirst } from '../utils/sentryGrouping';
import { performLogout } from '../utils/auth';
import { getActingRole } from '../utils/actingRoleStore';
import { AuthErrorCode } from '../enums/shared.enums';

// **** NETWORK LINKS ****
const errorLink = new ErrorLink(({ error, operation }: ErrorLink.ErrorHandlerOptions): void => {
    const operationName = operation.operationName || 'anonymous';
    const { operationType } = operation;

    // GraphQL errors are encapsulated in a CombinedGraphQLErrors instance in v4.
    if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path, extensions }) => {
            const code = typeof extensions?.code === 'string' ? extensions.code : undefined;
            const graphqlError = new Error(`[GraphQL error]: ${message}`);
            graphqlError.name = `GraphQLError(${operationName})`;

            Sentry.captureException(graphqlError, {
                tags: {
                    'graphql.operation': operationName,
                    'graphql.type': operationType,
                    ...(code && { 'graphql.code': code }),
                },
                fingerprint: ['graphql', operationName, ...(code ? [code] : []), normalizeMessage(message)],
                contexts: {
                    graphql: {
                        operationName,
                        operationType,
                        code,
                        path: path?.join('.'),
                        locations: locations?.map(({ line, column }) => `${line}:${column}`).join(', '),
                        variables: operation.variables,
                        query: toOperationFirst(operation.query).document,
                    },
                },
            });
        });
        return;
    }

    // Network errors are no longer wrapped and are returned as-is.
    const isServerError = ServerError.is(error);

    if (isServerError && error.statusCode === 401) {
        let code: string | undefined;
        try {
            const result = JSON.parse(error.bodyText) as Record<string, unknown> | undefined;
            code = result?.code as string | undefined;
        } catch {
            // Non-JSON response body; leave `code` undefined.
        }

        if (code === AuthErrorCode.TOKEN_EXPIRED) {
            performLogout({ reload: true });
        }
        return;
    }

    const errorMessage = error.message || 'Unknown network error';
    const extra: Record<string, unknown> = {
        message: errorMessage,
    };

    if (isServerError) {
        if (error.statusCode !== undefined) {
            extra.statusCode = error.statusCode;
        }
        if (error.stack) {
            extra.stack = error.stack;
        }
        if (error.response?.url) {
            extra.location = error.response.url;
        }
    }

    Sentry.captureException(error, {
        tags: { 'graphql.operation': operationName, 'graphql.type': operationType },
        fingerprint: [
            'graphql-network',
            operationName,
            isServerError && error.statusCode !== undefined ? String(error.statusCode) : error.name,
        ],
        contexts: {
            extra,
            graphql: {
                operationName,
                operationType,
                variables: operation.variables,
            },
        },
    });
});

const httpLink = new HttpLink({ uri: GQL_API, credentials: 'include' });

// AUTH-LINK
const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem('accessToken');

    operation.setContext({
        headers: {
            ...operation.getContext().headers,
            Authorization: token ? `${token}` : null,
        },
    });
    return forward(operation);
});

// ACTING ROLE LINK
const actingRoleLink = new ApolloLink((operation, forward) => {
    const actingRole = getActingRole();

    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'X-Acting-Role': actingRole,
        },
    }));

    return forward(operation);
});

const removeTypenameLink = new RemoveTypenameFromVariablesLink();

const link = ApolloLink.from([removeTypenameLink, authLink, actingRoleLink, errorLink, httpLink]);

const client = new ApolloClient({
    devtools: {
        enabled: true,
        name: 'Reachlink Web Frontend',
    },
    link,
    cache: new InMemoryCache({
        typePolicies: {
            Message: {
                keyFields: ['messageId'],
            },
            MessagingConversation: {
                keyFields: ['conversationId'],
            },
            Query: {
                fields: {
                    getMessagingConversation: {
                        read(existing, { args, toReference }) {
                            if (args?.input?.conversationId) {
                                return toReference({
                                    __typename: 'MessagingConversation',
                                    conversationId: args.input.conversationId,
                                });
                            }
                            return existing;
                        },
                    },
                    getConversationMessageList: {
                        keyArgs: ['filters', ['conversationId']],
                        merge(existing, incoming, { args }) {
                            if (!existing) return incoming;

                            const existingItems: readonly any[] = existing.items ?? [];
                            const incomingItems: readonly any[] = incoming.items ?? [];

                            // before cursor = older messages → server guarantees no overlap,
                            // append to end and preserve startCursor (newest cursor)
                            if (args?.pagination?.before) {
                                return {
                                    ...incoming,
                                    pageInfo: {
                                        ...incoming.pageInfo,
                                        startCursor: existing.pageInfo.startCursor,
                                    },
                                    items: [...existingItems, ...incomingItems],
                                };
                            }

                            // after cursor, cache writes, initial
                            // top-up → deduplicate and prepend to front
                            const existingIds = new Set(
                                existingItems.map((item: any) => item.__ref ?? item.messageId)
                            );
                            const uniqueIncoming = incomingItems.filter(
                                (item: any) => !existingIds.has(item.__ref ?? item.messageId)
                            );

                            return {
                                ...incoming,
                                pageInfo: {
                                    ...existing.pageInfo,
                                    // advance startCursor whenever the server returns items
                                    // (even if all are dupes) so fetchMoreMessages doesn't
                                    // keep re-fetching messages already in the cache
                                    ...(incomingItems.length > 0 && {
                                        startCursor: incoming.pageInfo.startCursor,
                                        hasPreviousPage: incoming.pageInfo.hasPreviousPage,
                                    }),
                                },
                                items: [...uniqueIncoming, ...existingItems],
                            };
                        },
                    },
                    providerCaseloadWithAppointments: {
                        keyArgs: ['providerUserId'],
                        merge(existing, incoming, { args }) {
                            if (!existing || !args?.cursor) {
                                return incoming;
                            }
                            return {
                                ...incoming,
                                edges: [...existing.edges, ...incoming.edges],
                            };
                        },
                    },
                },
            },
        },
    }),
});
export { client };

